import type { Row, RowData, TableFeatures } from "@tanstack/react-table";
import { type DataGridDisplayTextMeta, toSearchText } from "./cellValues";

const textsByColumns = new WeakMap<
    ReadonlyMap<string, DataGridDisplayTextMeta>,
    WeakMap<object, string>
>();

const buildRowSearchText = <
    TFeatures extends TableFeatures,
    TData extends RowData,
>(
    row: Row<TFeatures, TData>,
    metaById: ReadonlyMap<string, DataGridDisplayTextMeta>,
): string => {
    const texts: string[] = [];
    for (const [columnId, meta] of metaById) {
        const text = toSearchText(row.getValue(columnId), meta);
        if (text !== null) {
            texts.push(text);
        }
    }
    return texts.join("\n").toLowerCase();
};

/**
 * Everything search matches in one row, lowercased: each searchable column's
 * `toSearchText`, one per line so a query can't span two columns. Built once
 * per row object and `metaById` (the searchable columns, by identity), so a
 * search pass is one `includes` per row. New or edited row objects rebuild.
 */
export const getRowSearchText = <
    TFeatures extends TableFeatures,
    TData extends RowData,
>(
    row: Row<TFeatures, TData>,
    metaById: ReadonlyMap<string, DataGridDisplayTextMeta>,
): string => {
    const original = row.original;
    if (typeof original !== "object" || original === null) {
        return buildRowSearchText(row, metaById);
    }
    let texts = textsByColumns.get(metaById);
    if (texts === undefined) {
        texts = new WeakMap();
        textsByColumns.set(metaById, texts);
    }
    const cached = texts.get(original);
    if (cached !== undefined) {
        return cached;
    }
    const text = buildRowSearchText(row, metaById);
    texts.set(original, text);
    return text;
};
